import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import WtiLoader from '../components/WtiLoader';

type MagicLoginState = 'loading' | 'success' | 'error';

/** Resolve token from query (?token=), path param, or legacy glued paths. */
function resolveMagicToken(
  searchParams: URLSearchParams,
  pathname: string,
  pathToken?: string
): string | undefined {
  const fromQuery = searchParams.get('token')?.trim();
  if (fromQuery) {
    return fromQuery;
  }

  const fromPath = pathToken?.trim();
  if (fromPath) {
    return fromPath;
  }

  // Legacy: /auth/magic{64-hex} when "?token=" was stripped by an email client
  const glued = pathname.match(/^\/auth\/magic([a-f0-9]{64})$/i);
  if (glued) {
    return glued[1];
  }

  return undefined;
}

const MagicLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams<{ token?: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { loginWithMagic } = useAuth();
  const [state, setState] = useState<MagicLoginState>('loading');
  const [errorMessage, setErrorMessage] = useState('Invalid link');

  const backgroundImageUrl = `${process.env.PUBLIC_URL}/images/login-bg.png`;

  const authenticate = useCallback(async () => {
    const token = resolveMagicToken(searchParams, pathname, pathToken);

    if (!token) {
      setErrorMessage('Invalid link');
      setState('error');
      return;
    }

    try {
      await loginWithMagic(token);
      setState('success');
      toast.success('Welcome to WTI!');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        'This sign-in link is not valid. Please request a new access email.';
      setErrorMessage(message);
      setState('error');
    }
  }, [loginWithMagic, navigate, pathToken, pathname, searchParams]);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  return (
    <div style={styles.wrapper}>
      <div
        style={{
          ...styles.bgImage,
          backgroundImage: `url(${backgroundImageUrl})`,
        }}
      />
      <div style={styles.overlay} />

      <div style={styles.cardOuter}>
        <div style={styles.card}>
          <div style={styles.logoSection}>
            <div style={styles.brandRow}>
              <span style={styles.brandLetter}>w</span>
              <span style={styles.brandLetter}>t</span>
              <span style={styles.brandLetter}>i</span>
            </div>
            <p style={styles.tagline}>Where To Invest</p>
          </div>

          {state === 'loading' && (
            <>
              <h1 style={styles.heading}>Signing you in</h1>
              <p style={styles.subtext}>Please wait while we verify your secure access link.</p>
              <WtiLoader size="sm" />
            </>
          )}

          {state === 'error' && (
            <>
              <div style={styles.errorIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h1 style={styles.heading}>Access link unavailable</h1>
              <p style={styles.subtext}>{errorMessage}</p>
              <p style={styles.helpText}>
                If your link expired or was already used, check your email for the latest WTI access message or contact support.
              </p>
              <Link to="/login" style={styles.primaryLink}>
                Go to login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  bgImage: {
    position: 'absolute',
    inset: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    zIndex: 0,
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    zIndex: 1,
  },
  cardOuter: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 440,
    padding: '24px',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 32px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    textAlign: 'center',
  },
  logoSection: {
    marginBottom: 24,
  },
  brandRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 2,
    marginBottom: 8,
  },
  brandLetter: {
    fontSize: 32,
    fontWeight: 700,
    color: '#1a73e8',
    textTransform: 'lowercase',
  },
  tagline: {
    margin: 0,
    color: '#5f6368',
    fontSize: 14,
  },
  heading: {
    margin: '0 0 12px',
    fontSize: 24,
    color: '#202124',
  },
  subtext: {
    margin: '0 0 16px',
    color: '#5f6368',
    lineHeight: 1.5,
  },
  helpText: {
    margin: '0 0 24px',
    color: '#80868b',
    fontSize: 14,
    lineHeight: 1.5,
  },
  spinner: {
    width: 36,
    height: 36,
    margin: '16px auto 0',
    border: '3px solid #e8eaed',
    borderTopColor: '#1a73e8',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorIcon: {
    marginBottom: 16,
  },
  primaryLink: {
    display: 'inline-block',
    padding: '12px 24px',
    background: '#1a73e8',
    color: '#fff',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default MagicLogin;
